/**
 * Typed client for the v1 platform API — scoped API keys and strategy feeds.
 *
 * Both routers accept either a `pa_` API key or the session JWT (see
 * `backend/services/api_key_auth.py`), so the dashboard authenticates with the
 * same `authToken` every other page uses.
 *
 * Shapes here mirror the backend serializers exactly:
 *   - keys:  `backend/routers/api_keys.py::_public`
 *   - feeds: `backend/routers/feeds.py::serialize_feed` / `_emission_row` / `_screen_summary`
 */

import { getApiUrl, extractErrorMessage } from '../utils/api';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** The four scopes `api_key_service.VALID_SCOPES` accepts. Anything else is a 422. */
export const VALID_SCOPES = [
  'sweeps:read',
  'sweeps:write',
  'feeds:read',
  'feeds:write',
] as const;

export type ApiScope = (typeof VALID_SCOPES)[number];

export const SCOPE_DESCRIPTIONS: Record<ApiScope, string> = {
  'sweeps:read': 'List sweeps and read their results, receipts, and status.',
  'sweeps:write': 'Launch, cancel, and manage GPU parameter sweeps.',
  'feeds:read': 'List strategy feeds, pair screens, and the emission audit trail.',
  'feeds:write': 'Create and update feeds, upload screens, and sign emissions.',
};

export interface ApiKey {
  id: number;
  name: string;
  key_prefix: string;
  scopes: string[];
  rate_limit_per_min: number;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

/** Only the create response carries `secret`, and only once. */
export interface CreatedApiKey extends ApiKey {
  secret: string;
}

export interface ScreenSummary {
  screen_id: string;
  data_through: string;
  n_survivors: number;
  /** Present on `/screens` rows; absent from the `latest_screen` inline summary. */
  created_at?: string;
}

export interface StrategyFeed {
  feed_id: string;
  strategy_id: string;
  name: string;
  params: Record<string, unknown>;
  cadence: string;
  execution_url: string;
  key_id: string;
  enabled: boolean;
  created_at: string;
  updated_at: string | null;
  latest_screen: ScreenSummary | null;
}

export interface EmissionAttempt {
  at?: string | null;
  status?: number | null;
  latency_ms?: number | null;
  error?: string | null;
}

export interface SignalEmission {
  emission_id: string;
  feed_id: string;
  screen_id: string | null;
  generated_at: string;
  data_through: string;
  payload_sha256: string;
  dry_run: boolean;
  delivered: boolean;
  attempts: EmissionAttempt[] | null;
  response_status: number | null;
  latency_ms: number | null;
  created_at: string;
}

export interface Page<T> {
  items: T[];
  total: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Transport
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Thrown for any non-2xx response. `status` is kept so callers can tell a 401
 * (session expired — bounce to login) from a 422 (bad input — show the message).
 */
export class PlatformApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'PlatformApiError';
    this.status = status;
  }
}

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function request<T>(
  path: string,
  init: RequestInit = {},
  signal?: AbortSignal
): Promise<T> {
  const hasBody = init.body !== undefined;
  const response = await fetch(`${getApiUrl()}${path}`, {
    ...init,
    signal,
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
      ...(init.headers || {}),
    },
  });

  if (!response.ok) {
    // A 422 from these routers carries `{detail: {message, problems}}`; the
    // generic extractor handles the flat shapes, so unpack `problems` first.
    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      // non-JSON error body — fall through to the status-only message
    }
    const detail = payload?.detail;
    if (detail && typeof detail === 'object' && Array.isArray(detail.problems)) {
      const message = [detail.message, ...detail.problems].filter(Boolean).join(': ');
      throw new PlatformApiError(message, response.status);
    }
    throw new PlatformApiError(
      extractErrorMessage(payload, `Request failed (${response.status})`),
      response.status
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// API keys — /api/v1/keys
// ─────────────────────────────────────────────────────────────────────────────

export const listApiKeys = (signal?: AbortSignal): Promise<ApiKey[]> =>
  request<ApiKey[]>('/api/v1/keys', { method: 'GET' }, signal);

export const createApiKey = (
  name: string,
  scopes: string[],
  rateLimitPerMin: number
): Promise<CreatedApiKey> =>
  request<CreatedApiKey>('/api/v1/keys', {
    method: 'POST',
    body: JSON.stringify({ name, scopes, rate_limit_per_min: rateLimitPerMin }),
  });

export const revokeApiKey = (keyId: number): Promise<void> =>
  request<void>(`/api/v1/keys/${keyId}`, { method: 'DELETE' });

// ─────────────────────────────────────────────────────────────────────────────
// Feeds — /api/v1/feeds
// ─────────────────────────────────────────────────────────────────────────────

export const listFeeds = (signal?: AbortSignal): Promise<Page<StrategyFeed>> =>
  request<Page<StrategyFeed>>('/api/v1/feeds?limit=200', { method: 'GET' }, signal);

/**
 * The only call that populates `latest_screen` — the list serializer passes
 * `None` for it, so a list row's summary has to come from here.
 */
export const getFeed = (feedId: string, signal?: AbortSignal): Promise<StrategyFeed> =>
  request<StrategyFeed>(`/api/v1/feeds/${encodeURIComponent(feedId)}`, { method: 'GET' }, signal);

export const setFeedEnabled = (feedId: string, enabled: boolean): Promise<StrategyFeed> =>
  request<StrategyFeed>(`/api/v1/feeds/${encodeURIComponent(feedId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });

export const listScreens = (
  feedId: string,
  signal?: AbortSignal
): Promise<Page<ScreenSummary>> =>
  request<Page<ScreenSummary>>(
    `/api/v1/feeds/${encodeURIComponent(feedId)}/screens?limit=50`,
    { method: 'GET' },
    signal
  );

export const listEmissions = (
  feedId: string,
  signal?: AbortSignal
): Promise<Page<SignalEmission>> =>
  request<Page<SignalEmission>>(
    `/api/v1/feeds/${encodeURIComponent(feedId)}/emissions?limit=50`,
    { method: 'GET' },
    signal
  );
